/**
 * Modul-Status-Berechnung aus `ModuleState[]`.
 *
 * Übersetzt die rohen Step-Records der Workshop-Module in eine kompakte
 * Status-Aussage pro Modul (pending / in_progress / completed) plus einen
 * Fortschrittswert in Prozent. Verwendet vom Stepper und ggf. künftigen
 * Dashboard-Snapshots.
 */
import type { ModuleKey, ModuleState } from "@/api/workshop-modules";

export type ModuleStatusKind = "pending" | "in_progress" | "completed";

export interface ModuleProgress {
  status: ModuleStatusKind;
  /** 0–1: anteilig erreichte Steps (für sichtbare Progress-Bar im Stepper). */
  progress: number;
  /** Aktueller Step (1-basiert) im aktiven Modul; null wenn pending oder completed. */
  currentStep: number | null;
  /** Anzahl benötigter Steps für „completed". */
  totalSteps: number;
}

export const MODULE_ORDER: ModuleKey[] = [
  "archetypes",
  "golden_circle",
  "personas",
  "brandwheel",
  "sales_story",
];

const MODULE_ROUTE: Record<ModuleKey, string> = {
  archetypes: "archetypes",
  golden_circle: "golden-circle",
  personas: "personas",
  brandwheel: "brandwheel",
  sales_story: "sales-story",
};

export function moduleRoute(workshopId: string, key: ModuleKey): string {
  return `/workshops/${workshopId}/modules/${MODULE_ROUTE[key]}`;
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-Step-Definitionen — pro Modul die einzelnen Schritte (für den
// Pro-Modul-Stepper, der unter dem aktiven Modul aufklappt). Labels sind
// i18n-Keys aus dem `module_steps.*`-Namespace.
// ────────────────────────────────────────────────────────────────────────────

export interface ModuleSubStep {
  /** i18n-Key relativ zu `module_steps.<module_key>.` */
  labelKey: string;
  completed: boolean;
}

export function computeSubSteps(
  key: ModuleKey,
  allStates: ModuleState[],
): ModuleSubStep[] {
  const states = allStates.filter((s) => s.module_key === key);
  switch (key) {
    case "archetypes":
      return [
        { labelKey: "skills", completed: hasCompletedStep(states, 1) },
        { labelKey: "assign", completed: hasCompletedStep(states, 2) },
        { labelKey: "vote", completed: hasCompletedStep(states, 3) },
      ];
    case "golden_circle":
      return [
        { labelKey: "why", completed: hasSelected(states, 1) },
        { labelKey: "how", completed: hasSelected(states, 2) },
        { labelKey: "what", completed: hasSelected(states, 3) },
      ];
    case "personas": {
      const groups = readTargetGroups(states);
      const personas = readPersonas(states);
      return [
        { labelKey: "target_groups", completed: groups.length > 0 },
        {
          labelKey: "personas",
          completed:
            groups.length > 0 && groups.every((_, i) => personas[i] != null),
        },
      ];
    }
    case "brandwheel":
      return [
        { labelKey: "brand_core", completed: hasItems(states, 1) },
        { labelKey: "benefits", completed: hasItems(states, 2) },
        { labelKey: "tone", completed: hasItems(states, 3) },
        { labelKey: "attributes", completed: hasItems(states, 4) },
        { labelKey: "image", completed: hasItems(states, 5) },
      ];
    case "sales_story": {
      const targetCount = readTargetCountFromStates(allStates);
      const stories = readStories(states);
      const completed =
        targetCount > 0 &&
        stories.length >= targetCount &&
        stories.slice(0, targetCount).every((x) => x != null);
      return [{ labelKey: "generate", completed }];
    }
  }
}

function hasCompletedStep(states: ModuleState[], step: number): boolean {
  return states.find((s) => s.step === step)?.status === "completed";
}

function hasSelected(states: ModuleState[], step: number): boolean {
  const st = states.find((s) => s.step === step);
  if (!st || st.status !== "completed") return false;
  return typeof (st.result as GoldenCircleStepResult | null)?.selected_index === "number";
}

function hasItems(states: ModuleState[], step: number): boolean {
  const st = states.find((s) => s.step === step);
  if (!st || st.status !== "completed") return false;
  const items = (st.result as BrandwheelStepResult | null)?.items;
  return Array.isArray(items) && items.length > 0;
}

function readTargetGroups(states: ModuleState[]): unknown[] {
  const st = states.find((s) => s.step === 1);
  const tg = (st?.result as PersonasStep1Result | null)?.target_groups;
  return Array.isArray(tg) ? tg : [];
}

function readPersonas(states: ModuleState[]): unknown[] {
  const st = states.find((s) => s.step === 2);
  const personas = (st?.result as PersonasStep2Result | null)?.personas;
  return Array.isArray(personas) ? personas : [];
}

function readTargetCountFromStates(allStates: ModuleState[]): number {
  const personaStep1 = allStates.find(
    (s) => s.module_key === "personas" && s.step === 1,
  );
  const tg = (personaStep1?.result as PersonasStep1Result | null)?.target_groups;
  return Array.isArray(tg) ? tg.length : 0;
}

function readStories(states: ModuleState[]): unknown[] {
  const st = states.find((s) => s.step === 1);
  const stories = (st?.result as SalesStoryResult | null)?.stories;
  return Array.isArray(stories) ? stories : [];
}

const STEP_COUNTS: Record<ModuleKey, number> = {
  archetypes: 3,
  golden_circle: 3,
  personas: 2,
  brandwheel: 5,
  sales_story: 1,
};

interface PersonasStep1Result {
  target_groups?: unknown[];
}
interface PersonasStep2Result {
  personas?: unknown[];
}
interface SalesStoryResult {
  stories?: unknown[];
}
interface GoldenCircleStepResult {
  selected_index?: number | null;
}
interface BrandwheelStepResult {
  items?: unknown[];
}

function isComplete_archetypes(states: ModuleState[]): boolean {
  return [1, 2, 3].every(
    (n) => states.find((s) => s.step === n)?.status === "completed",
  );
}

function isComplete_golden_circle(states: ModuleState[]): boolean {
  return [1, 2, 3].every((n) => {
    const st = states.find((s) => s.step === n);
    if (!st || st.status !== "completed") return false;
    const r = st.result as GoldenCircleStepResult | null;
    return typeof r?.selected_index === "number";
  });
}

function isComplete_personas(states: ModuleState[]): boolean {
  const step1 = states.find((s) => s.step === 1);
  const step2 = states.find((s) => s.step === 2);
  if (!step1 || step1.status !== "completed") return false;
  if (!step2 || step2.status !== "completed") return false;
  const groups = (step1.result as PersonasStep1Result | null)?.target_groups;
  const personas = (step2.result as PersonasStep2Result | null)?.personas;
  if (!Array.isArray(groups) || groups.length === 0) return false;
  if (!Array.isArray(personas)) return false;
  // Jede Zielgruppe braucht eine Persona (Slot != null).
  return groups.every((_, i) => personas[i] != null);
}

function isComplete_brandwheel(states: ModuleState[]): boolean {
  return [1, 2, 3, 4, 5].every((n) => {
    const st = states.find((s) => s.step === n);
    if (!st || st.status !== "completed") return false;
    const items = (st.result as BrandwheelStepResult | null)?.items;
    return Array.isArray(items) && items.length > 0;
  });
}

function isComplete_sales_story(
  states: ModuleState[],
  expectedCount: number | null,
): boolean {
  const st = states.find((s) => s.step === 1);
  if (!st || st.status !== "completed") return false;
  const stories = (st.result as SalesStoryResult | null)?.stories;
  if (!Array.isArray(stories) || stories.length === 0) return false;
  if (expectedCount === null) return stories.some((x) => x != null);
  return stories.length >= expectedCount && stories.slice(0, expectedCount).every((x) => x != null);
}

/**
 * Liefert für jedes der fünf Module eine Progress-Aussage.
 *
 * Personas und Sales-Story hängen voneinander ab (Sales-Story braucht alle
 * Personas), darum wird `personas_target_count` aus dem Personas-Step
 * mitgegeben.
 */
export function computeModuleProgress(allStates: ModuleState[]): Record<ModuleKey, ModuleProgress> {
  const personaStep1 = allStates.find(
    (s) => s.module_key === "personas" && s.step === 1,
  );
  const personaTargetCount =
    Array.isArray((personaStep1?.result as PersonasStep1Result | null)?.target_groups)
      ? ((personaStep1!.result as PersonasStep1Result).target_groups as unknown[]).length
      : null;

  const out = {} as Record<ModuleKey, ModuleProgress>;
  for (const key of MODULE_ORDER) {
    const states = allStates.filter((s) => s.module_key === key);
    out[key] = computeOne(key, states, personaTargetCount);
  }
  return out;
}

function computeOne(
  key: ModuleKey,
  states: ModuleState[],
  personaTargetCount: number | null,
): ModuleProgress {
  const total = STEP_COUNTS[key];
  if (states.length === 0) {
    return { status: "pending", progress: 0, currentStep: null, totalSteps: total };
  }

  let complete = false;
  switch (key) {
    case "archetypes":
      complete = isComplete_archetypes(states);
      break;
    case "golden_circle":
      complete = isComplete_golden_circle(states);
      break;
    case "personas":
      complete = isComplete_personas(states);
      break;
    case "brandwheel":
      complete = isComplete_brandwheel(states);
      break;
    case "sales_story":
      complete = isComplete_sales_story(states, personaTargetCount);
      break;
  }

  if (complete) {
    return { status: "completed", progress: 1, currentStep: null, totalSteps: total };
  }

  // in_progress: zähle abgeschlossene Steps; aktueller Step = niedrigster
  // Step der noch nicht „completed" ist (oder Steps+1 wenn alle „completed"
  // sind, aber Inhalts-Validation noch fehlt — was bei completed schon
  // ausgeschlossen wäre).
  const completedSteps = states.filter((s) => s.status === "completed").length;
  const ratio = Math.min(completedSteps / total, 0.95);
  const nextIncomplete = [...Array(total).keys()]
    .map((i) => i + 1)
    .find((n) => {
      const st = states.find((s) => s.step === n);
      return !st || st.status !== "completed";
    });
  return {
    status: "in_progress",
    progress: ratio,
    currentStep: nextIncomplete ?? null,
    totalSteps: total,
  };
}
